<script setup>
import NewInput from "./NewInput.vue";
import { nextTick, ref, watch } from "vue";
import { debounce } from "./utils";
import { createPopper } from "@popperjs/core";
import SimpleLoadingCircle from "../SimpleLoadingCircle.vue";

const inputRef = ref(null);
const popperRef = ref(null);
const popperHandle = ref(null);

const emit = defineEmits(["select"]);
const props = defineProps({
    modelValue: {
        type: String,
        required: false,
        default: undefined,
    },
});
const model = ref(props.modelValue);
const query = ref(props.modelValue ?? "");
const focus = ref(false);
const loading = ref(false);
const users = ref([]);

function selectUser(user) {
    emit("select", user);
    emit("update:modelValue", user);
    model.value = user;
    nextTick(() => {
        focus.value = false;
        inputRef.value.$el.blur();
    });
}

function validEmail(email) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}

const tryAutocomplete = debounce(() => {
    if (query.value.length < 3) {
        return;
    }

    loading.value = true;
    users.value = [];
    axios
        .get("/utils/autocomplete-user", {
            params: {
                query: query.value,
            },
        })
        .then((response) => {
            users.value = response.data;
        })
        .finally(() => {
            loading.value = false;
        });
}, 500);

watch(focus, () => {
    if (focus.value) {
        nextTick(() => {
            popperHandle.value = createPopper(
                inputRef.value.$el,
                popperRef.value,
                {
                    placement: "bottom-start",
                    modifiers: [
                        {
                            name: "offset",
                            options: {
                                offset: [0, 8],
                            },
                        },
                    ],
                }
            );
            popperHandle.value.forceUpdate();
        });
    }
});
</script>

<template>
    <new-input
        v-bind="$attrs"
        placeholder="E-mail or User"
        required
        type="email"
        class="flex-grow flex-shrink"
        ref="inputRef"
        v-model="query"
        @focusin="focus = true"
        @focusout="focus = false"
        @input="tryAutocomplete"
        v-show="typeof model !== 'object'"
    />
    <div
        v-if="typeof model === 'object'"
        class="
            text-base
            bg-gray-100
            py-2
            px-2
            font-sans
            rounded-md
            w-full
            hover:border-gray-300
            focus:border-primary
            flex
            items-center
            gap-2
        "
    >
        <img
            :src="model.avatar"
            :alt="model.name"
            class="w-8 h-8 aspect-square rounded-full"
        />
        {{ model.name }}
        <button
            type="button"
            class="
                p-2
                ml-auto
                rounded-lg
                flex
                items-center
                bg-gray-200
                hover:bg-gray-300
            "
            @click="
                model = undefined;
                query = '';
            "
        >
            <ion-icon name="close-outline" class="text-gray-500"></ion-icon>
        </button>
    </div>
    <Teleport to="body">
        <div
            ref="popperRef"
            v-show="focus || (query.length > 0 && !model)"
            class="z-[1000] bg-white shadow rounded-md p-2 max-w-[100vw]"
            :style="{
                width: inputRef?.$el?.offsetWidth + 'px',
            }"
        >
            <div
                class="text-xs text-gray-500 my-2"
                v-if="users.length === 0 && query.length < 3"
            >
                Enter the user e-mail address or type at least 3 characters to
                search for a user.
            </div>
            <div
                class="text-xs text-gray-500 my-2"
                v-if="
                    users.length === 0 &&
                    query.length >= 3 &&
                    !loading &&
                    !validEmail(query)
                "
            >
                No users found with the keyword "{{ query }}".
            </div>
            <div
                class="
                    text-gray-500
                    my-2
                    p-2
                    hover:bg-gray-100
                    cursor-pointer
                    select-none
                    text-xs
                    rounded-lg
                "
                v-if="
                    users.length === 0 &&
                    query.length >= 3 &&
                    !loading &&
                    validEmail(query)
                "
                @click="selectUser(query)"
            >
                Invite external user "{{ query }}".
            </div>
            <simple-loading-circle v-if="loading"></simple-loading-circle>
            <div class="my-2">
                <div
                    v-for="user in users"
                    class="
                        flex
                        items-center
                        gap-4
                        my-1
                        rounded-md
                        border-b border-b-gray-200
                        p-2
                        hover:bg-blue-50
                        cursor-pointer
                        select-auto
                    "
                    @click="selectUser(user)"
                >
                    <img
                        :src="user.avatar"
                        :alt="user.name"
                        class="w-8 h-8 aspect-square rounded-full"
                    />
                    {{ user.name }}
                </div>
            </div>
        </div>
    </Teleport>
</template>

<style scoped></style>
